import React from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { Url, CHECKOUT_IDS } from '@wix/da-url';
import { PapiUser } from '@wix/da-papi-types';
import { actions as modalActions } from '@wix/da-shared-react/pkg/Modals';
import { BiEventType, ReferralType } from '@wix/da-bi/pkg/constants';
import IconButtonWithContextMenu from '@wix/da-shared-react/pkg/Dropdown/Preset/IconButtonWithContextMenuV2';
import { RestrictLoggedOutPayload } from '@wix/da-shared-react/pkg/redux/loggedout/actionCreators';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import GiftIcon from '@wix/da-ds/pkg/Icons/24x24/Gift';
import CoreStar from '@wix/da-ds/pkg/Icons/CoreStar';
import LlamaIcon from '@wix/da-ds/pkg/Icons/Llama';
import CakeIcon from '@wix/da-ds/pkg/Icons/24x24/Cake';
import PointsIcon from '@wix/da-ds/pkg/Icons/Points';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import { ProfileOwnerGiveState } from '../../../../../../../../types/profileOwner';
import s from './GiveButton.scss';

export interface Props {
  className?: string;
  isLoggedIn?: boolean;
  profileOwnerUser: PapiUser;
  giveState?: ProfileOwnerGiveState;
  restrictLoggedOut: (payload: RestrictLoggedOutPayload) => void;
  onLlamaBadgeSent: () => void;
  initGiveState: () => void;
  pushBadgeModal: typeof modalActions.pushBadgeModal;
  pushPointsModal: (username: string, title: string) => void;
}

const GiveButton: React.FC<Props> = ({
  className,
  isLoggedIn,
  giveState,
  initGiveState,
  onLlamaBadgeSent,
  profileOwnerUser: { username, userId, useridUuid },
  pushBadgeModal,
  pushPointsModal,
  restrictLoggedOut,
}) => {
  const { t } = useTranslation();

  const getGiveMenuItems = () => {
    if (!giveState) {
      return [];
    }

    const coreLabel = giveState.canBeGivenCore
      ? t('navigation.subnavigation.giveCore')
      : t('navigation.subnavigation.hasCore');
    const pointsLabel = t('navigation.subnavigation.givePoints');
    const lamaLabel = giveState.canBeGivenLlama
      ? t('navigation.subnavigation.giveLlamaBadge')
      : t('navigation.subnavigation.hasGotLlamaBadge');
    const cakeLabel = giveState.canBeGivenCake
      ? t('navigation.subnavigation.giveCakeBadge')
      : t('navigation.subnavigation.hasGotCakeBadge');
    const biData = {
      evid: BiEventType.GIFT_SELECT_CLICK,
      recipientid: useridUuid,
    };
    const giveCoreUrl = Url.giveCoreMembership(username);

    return [
      {
        icon: giveState.isLoading ? (
          <LoadingIndicator />
        ) : (
          <CoreStar size={IconSize.SMALL} />
        ),
        iconClassName: c(
          s['core-icon'],
          giveState.isLoading && s['loading-spinner']
        ),
        label: coreLabel,
        biData: {
          ...biData,
          menu_item_name: coreLabel,
          link_url: giveCoreUrl,
        },
        onClick: () => window.location.assign(giveCoreUrl),
        disabled: !giveState.canBeGivenCore,
      },
      {
        icon: <PointsIcon />,
        label: pointsLabel,
        biData: { ...biData, menu_item_name: pointsLabel },
        onClick: () => {
          pushPointsModal(username, pointsLabel);
        },
      },
      {
        icon: giveState.isLoading ? <LoadingIndicator /> : <LlamaIcon />,
        iconClassName: c(giveState.isLoading && s['loading-spinner']),
        label: lamaLabel,
        biData: { ...biData, menu_item_name: lamaLabel },
        onClick: () => {
          pushBadgeModal(username, onLlamaBadgeSent);
        },
        disabled: !giveState.canBeGivenLlama,
      },
      {
        icon: giveState.isLoading ? <LoadingIndicator /> : <CakeIcon />,
        iconClassName: c(giveState.isLoading && s['loading-spinner']),
        label: cakeLabel,
        biData: { ...biData, menu_item_name: cakeLabel },
        onClick: () => {
          window.location.assign(
            `${Url.checkoutLink(CHECKOUT_IDS.CAKE)}&foruserid=${userId}`
          );
        },
        disabled: !giveState.canBeGivenCake,
      },
    ];
  };

  return (
    <IconButtonWithContextMenu
      size="medium"
      icon={GiftIcon}
      aria-label={t('common.give')}
      className={className}
      items={getGiveMenuItems()}
      popperManagerProps={{
        onOpen: isLoggedIn
          ? initGiveState
          : () =>
              restrictLoggedOut({ restrictedActionName: ReferralType.GIVE }),
      }}
      biData={{
        evid: BiEventType.GIFT_CLICK,
        recipientid: useridUuid,
      }}
    />
  );
};
GiveButton.displayName = 'GiveButton';

export default GiveButton;
